import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClientIntegrationsTable } from '@/modules/clients/components/client-details/integrations';
import { useClientIntegrations } from '@/modules/clients/hooks/use-client-integrations';
import { usePermissions } from '@/modules/common';
import { EntityIntegrationsRefreshCard } from '@/modules/common/components/organisms/entity-integrations-refresh-card';
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  useBaseTab,
} from '@/modules/common/components/templates/feature-details';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityType } from '@/modules/common/types/entity-type';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import {
  Client,
  ClientFormData,
  ClientIntegration,
  ClientIntegrationsFormData,
  ClientStatus,
} from '../../../types';

export interface ClientIntegrationsTabProps
  extends BaseTabProps<Client, ClientIntegrationsFormData> {
  rawIntegrations?: IntegrationInfoResponse[];
  onQuickBooksCustomerPickedChange?: (isPicked: boolean) => void;
}

export interface ClientIntegrationsTabRef extends FeatureTabRef<ClientFormData> {}

const TAG = 'ClientIntegrationsTab:';

/**
 * Client integrations tab component supporting create, edit, and read modes.
 * Clean and simple implementation with proper data merging.
 */
export const ClientIntegrationsTab = forwardRef<
  ClientIntegrationsTabRef,
  ClientIntegrationsTabProps
>((props, ref) => {
  const {
    mode,
    featureData: clientsData,
    rawIntegrations,
    draftData,
    onQuickBooksCustomerPickedChange,
  } = props;

  const { t } = useTranslation(['authenticated.clients']);
  const { t: tCommon } = useTranslation('common');

  const [integrations, setIntegrations] = useState<ClientIntegration[]>([]);
  const [initialIntegrations, setInitialIntegrations] = useState<ClientIntegration[]>([]);

  // Use the base tab hook directly since this tab doesn't use a traditional form
  const { isReadMode } = useBaseTab(props, {
    isArchivedFn: client => client?.status === ClientStatus.Archived,
    calculateDirtyStateFn: () => compareFormValues(integrations, initialIntegrations),
  });

  // Fetch available integrations
  // In Edit/Read mode, merge with selected integrations from client data
  // In Create mode, just fetch unlinked integrations
  const {
    data: availableIntegrationsData,
    isLoading,
    error,
  } = useClientIntegrations({
    mode,
    clientId: clientsData?.id,
    rawIntegrations: rawIntegrations,
  });

  // Build final integrations data based on mode
  const modeBasedIntegrations = useMemo(() => {
    if (!availableIntegrationsData) return [];

    logger.debug(TAG, 'Building final integrations', { mode });

    if (mode === FeatureMode.Create) {
      // Create mode: merge with draft data if available
      if (draftData?.integrations) {
        return availableIntegrationsData.map(integration => {
          const draftIntegration = draftData.integrations?.find(di => di.id === integration.id);
          return {
            ...integration,
            selectedValue: draftIntegration?.selectedValue || null,
          };
        });
      }
      // No draft data, return as-is (no selections)
      return availableIntegrationsData;
    }

    // Edit/Read mode: data is already merged from query
    return availableIntegrationsData;
  }, [availableIntegrationsData, mode, draftData?.integrations]);

  // Update integrations state when final data changes
  useEffect(() => {
    if (modeBasedIntegrations.length > 0) {
      setIntegrations(modeBasedIntegrations);
      setInitialIntegrations(modeBasedIntegrations);
    }
  }, [modeBasedIntegrations]);

  useEffect(() => {
    onQuickBooksCustomerPickedChange?.(
      integrations.some(
        integration =>
          integration.type === IntegrationServices.QUICKBOOKS && Boolean(integration.selectedValue)
      )
    );
  }, [integrations, onQuickBooksCustomerPickedChange]);

  // Handle client selection change
  const handleClientChange = useCallback((integrationId: string, externalClientId?: string) => {
    logger.debug(TAG, 'Client selection changed', { integrationId, externalClientId });

    setIntegrations(prev => {
      return prev.map(integration => {
        if (integration.id === integrationId) {
          let selectedValue: typeof integration.selectedValue = null;

          // Find the selected client from the integration's clients
          if (externalClientId && integration.clients) {
            const selectedClient = integration.clients.find(p => p.id === externalClientId);
            selectedValue = selectedClient || null;
          }

          return {
            ...integration,
            selectedValue,
          };
        }
        return integration;
      });
    });
  }, []);

  /**
   * Custom form data getter for integrations
   */
  const getFormData = (): ClientFormData => {
    return {
      integrations,
    } as ClientFormData;
  };

  /**
   * Custom submit handler for integrations
   */
  const submitHandler = async (): Promise<void> => {
    logger.debug(TAG, 'Form submission requested');
    // No validation needed for integrations
  };

  const { hasPermission } = usePermissions();
  const canManageIntegrations = hasPermission(OpsiumPolicy.IntegrationsManage);

  const tabContent = (
    <div className="space-y-4">
      {/* Main integrations card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('details.tabs.integrations.title')}</CardTitle>
            {canManageIntegrations && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  window.open(`${window.location.origin}${SettingsRoutes.Integrations}`, '_blank');
                }}
                disabled={isReadMode}
              >
                {t('details.tabs.integrations.manageIntegrations')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <EntityIntegrationsRefreshCard
            entityType={EntityType.Client}
            disabled={isReadMode}
            t={t}
            strings={{
              buttonTitle: t('details.tabs.integrations.refreshClients'),
              buttonTitleRefreshing: t('details.tabs.integrations.refreshing'),
              descriptionI18nKey: 'details.tabs.integrations.refreshAlert',
              refreshButtonText: t('details.tabs.integrations.refreshButtonText'),
              successToast: t('details.tabs.integrations.refreshSuccess'),
              errorToast: t('details.tabs.integrations.refreshError'),
            }}
          />
          {/* Integrations table */}
          <ClientIntegrationsTable
            integrations={integrations}
            isLoading={isLoading}
            error={error ? tCommon('error.generic') : null}
            isReadMode={isReadMode}
            onClientChange={handleClientChange}
          />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <BaseFeatureTab
      {...props}
      ref={ref}
      isArchivedFn={client => client?.status === ClientStatus.Archived}
      onSubmit={submitHandler}
      isValid={true}
      getFormData={getFormData}
    >
      {tabContent}
    </BaseFeatureTab>
  );
});

ClientIntegrationsTab.displayName = 'ClientIntegrationsTab';
